import {
  getPageHeader,
  Card,
  CardContent,
  Button,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderActionSecondary,
  PageHeaderActions,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  VisibleFilterFields,
} from '@yeshuman/sdk'
import { useEffect, useRef, useState } from 'react'
import { HealthListPageSkeleton } from './health-list-page-skeleton'
import { Pill, Plus, Pencil, Trash2 } from 'lucide-react'
import {
  useTherapeutics,
  useCreateTherapeutic,
  useUpdateTherapeutic,
  useDeleteTherapeutic,
  type TherapeuticFilters,
  type TherapeuticItem,
  type TherapeuticTypeFilter,
  type CreateTherapeuticData,
  type UpdateTherapeuticData,
} from '../hooks/use-therapeutics'
import { toast } from 'sonner'
import { TherapeuticStats } from './therapeutic-stats'
import { TherapeuticFormDialog } from './therapeutic-form-dialog'
import { getHealthTherapeuticFilterDefinitions } from '../list-filters/therapeutics'

const therapeuticFilterDefinitions = getHealthTherapeuticFilterDefinitions()

function formatRecorded(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}

function summaryLine(t: TherapeuticItem): string {
  if (t.therapeutic_type === 'medication') {
    const parts = [t.name, t.dose_text, t.frequency_text].filter(Boolean)
    return parts.join(' · ') || 'Medication'
  }
  if (t.therapeutic_type === 'supplement') {
    return [t.name, t.dose_text].filter(Boolean).join(' · ') || 'Supplement'
  }
  if (t.therapeutic_type === 'therapy_session') {
    const d =
      t.duration_minutes != null ? `${t.duration_minutes} min` : null
    return [t.label, d].filter(Boolean).join(' · ') || 'Therapy session'
  }
  return ''
}

function typeLabel(tt: string) {
  if (tt === 'medication') return 'Medication'
  if (tt === 'supplement') return 'Supplement'
  if (tt === 'therapy_session') return 'Therapy session'
  return tt
}

export function TherapeuticsPage() {
  const [filters, setFilters] = useState<TherapeuticFilters>({ limit: 100 })
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<TherapeuticItem | null>(null)
  const [createDefaultType, setCreateDefaultType] = useState<TherapeuticTypeFilter>('medication')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [highlighted, setHighlighted] = useState<Set<string>>(new Set())
  const prevRef = useRef<TherapeuticItem[]>([])

  const { therapeutics, isLoading, error, refetch } = useTherapeutics(filters)
  const createT = useCreateTherapeutic()
  const updateT = useUpdateTherapeutic()
  const deleteT = useDeleteTherapeutic()

  useEffect(() => {
    if (therapeutics && prevRef.current.length > 0) {
      const changed = new Set<string>()
      therapeutics.forEach((row) => {
        const prev = prevRef.current.find((p) => p.id === row.id)
        if (!prev || JSON.stringify(prev) !== JSON.stringify(row)) changed.add(row.id)
      })
      if (changed.size > 0) {
        setHighlighted(changed)
        window.setTimeout(() => setHighlighted(new Set()), 3000)
      }
    }
    if (therapeutics) prevRef.current = therapeutics
  }, [therapeutics])

  const handleAdd = (tt: TherapeuticTypeFilter = 'medication') => {
    setEditing(null)
    setCreateDefaultType(tt)
    setFormOpen(true)
  }

  const handleEdit = (row: TherapeuticItem) => {
    setEditing(row)
    setFormOpen(true)
  }

  const onCreate = async (data: CreateTherapeuticData) => {
    await createT.mutateAsync(data)
  }

  const onUpdate = async (data: UpdateTherapeuticData) => {
    await updateT.mutateAsync(data)
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteT.mutateAsync(id)
      toast.success('Entry deleted')
      setDeletingId(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete')
    }
  }

  const { title, description } = getPageHeader(
    '/therapeutics',
    'Therapeutics',
    'Self-reported medications, supplements, and therapy sessions — wellness journaling, not prescribing.',
  )

  return (
    <PageContainer>
      <PageHeader
          leading={<PageHeaderLeading />}
          title={title}
          description={description}
          icon={<Pill className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              secondary={
                <>
                  <PageHeaderActionSecondary onClick={() => handleAdd('medication')}>
                    <Plus className="h-4 w-4" aria-hidden />
                    Medication
                  </PageHeaderActionSecondary>
                  <PageHeaderActionSecondary onClick={() => handleAdd('supplement')}>
                    <Plus className="h-4 w-4" aria-hidden />
                    Supplement
                  </PageHeaderActionSecondary>
                  <PageHeaderActionSecondary onClick={() => handleAdd('therapy_session')}>
                    <Plus className="h-4 w-4" aria-hidden />
                    Therapy
                  </PageHeaderActionSecondary>
                </>
              }
            />
          }
        />

        <TherapeuticStats therapeutics={therapeutics} />

        <VisibleFilterFields
          listId="healthTherapeutics"
          filters={filters}
          onFiltersChange={setFilters}
          definitions={therapeuticFilterDefinitions}
          ctx={undefined}
        />

        {error ? (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">Failed to load entries: {error.message}</p>
              <div className="mt-4">
                <Button variant="outline" onClick={() => void refetch()}>
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {isLoading ? <HealthListPageSkeleton showFilters={false} ariaLabel="Loading therapeutics" /> : null}

        {!isLoading && !error ? (
          therapeutics.length === 0 ? (
            <Card>
              <CardContent className="py-10 text-center">
                <Pill className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <h3 className="mb-2 text-lg font-semibold">No entries yet</h3>
                <p className="mb-4 text-muted-foreground">
                  Log medications, supplements, or therapy sessions you want to remember.
                </p>
                <Button onClick={() => handleAdd('medication')}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add first entry
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {therapeutics.map((row) => (
                <Card
                  key={row.id}
                  className={
                    highlighted.has(row.id)
                      ? 'border-primary ring-1 ring-primary/30 transition-all duration-500'
                      : ''
                  }
                >
                  <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="secondary">{typeLabel(row.therapeutic_type)}</Badge>
                        <span className="text-sm text-muted-foreground">{formatRecorded(row.recorded_at)}</span>
                      </div>
                      <p className="font-medium">{summaryLine(row)}</p>
                      {row.notes ? (
                        <p className="text-sm text-muted-foreground line-clamp-3">{row.notes}</p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button variant="outline" size="icon" type="button" onClick={() => handleEdit(row)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" type="button" onClick={() => setDeletingId(row.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )
        ) : null}

        <TherapeuticFormDialog
          open={formOpen}
          onOpenChange={(open) => {
            setFormOpen(open)
            if (!open) setEditing(null)
          }}
          therapeutic={editing}
          defaultType={createDefaultType}
          onCreate={onCreate}
          onUpdate={onUpdate}
        />

        <AlertDialog open={deletingId !== null} onOpenChange={(open) => !open && setDeletingId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
              <AlertDialogDescription>This removes it from your list. You cannot undo this.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => deletingId && void handleDelete(deletingId)}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </PageContainer>
  )
}
